/**
 * Bouton d'action flottant (FAB) par morphing du CTA, piloté par le scroll.
 *
 * Opt-in : actif seulement sur les éléments `data-cta-float` (posé sur le lien
 * CTA ou son conteneur). Au scroll, un calque reprend l'aspect du bouton puis
 * morphe en rond calé sous le menu, à droite. La position est dérivée du rect
 * réel du bouton à chaque frame, donc le calque le suit dans les deux sens.
 *
 * Source du morph : l'élément marqué `data-cta-float-source` s'il existe, sinon
 * le premier `a[href]` du conteneur. Si la source porte `data-dropdown-toggle`,
 * le FAB devient un bouton qui rouvre ce menu (positionné en fixed au-dessus du
 * FAB) au lieu de naviguer.
 */
export function initCtaFloat(): void {
    const markers = document.querySelectorAll<HTMLElement>('[data-cta-float]');
    if (markers.length === 0) {
        return;
    }

    const navbar = document.querySelector<HTMLElement>('[data-navigation="main-navigation"]');
    const navHeight = navbar?.offsetHeight || 64;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const GAP = 40; // marge droite (fallback)
    const EDGE_MIN = 16; // marge minimale avec le bord de l'écran (padding mobile du conteneur)
    const TOP_OFFSET = 24; // décalage sous le menu
    const TRIGGER_OFFSET = 40; // fenêtre de scroll du morph
    const FOOTER_PAD = 16; // padding entre le rond et le footer

    // Bord droit du contenu, pour aligner le rond dessus.
    const container = document.querySelector<HTMLElement>('[data-section] .max-w-7xl');
    const containerPadRight = container ? (parseFloat(getComputedStyle(container).paddingRight) || 0) : 0;
    // Footer, pour arrêter le rond au-dessus.
    const footer = document.querySelector<HTMLElement>('.bg-onyx-950');
    const HOVER_TRANSITION = 'outline-width 0.2s ease, outline-offset 0.2s ease, box-shadow 0.2s ease';

    // Flèche droite du DS, recolorée en currentColor.
    const ARROW = '<svg width="17" height="16" viewBox="0 0 17 16" fill="none" aria-hidden="true"><path d="M12.5861 9.99999L8.79314 13.793C8.40261 14.1835 8.40261 14.8165 8.79314 15.207C9.18366 15.5975 9.81668 15.5975 10.2072 15.207L15.4142 9.99999H12.5861Z" fill="currentColor"/><path d="M10.2072 0.792954C9.81668 0.40243 9.18366 0.40243 8.79314 0.792954C8.40261 1.18348 8.40261 1.81649 8.79314 2.20702L14.5861 7.99999L13.5861 8.99999H16.4142L16.7072 8.70702C17.0977 8.31649 17.0977 7.68348 16.7072 7.29295L10.2072 0.792954Z" fill="currentColor"/><path d="M16 8H1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    // Chevron bas pour les CTA de type dropdown (le FAB rouvre le menu).
    const CHEVRON = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const clamp = (v: number): number => Math.min(1, Math.max(0, v));
    const lerp = (a: number, b: number, p: number): number => a + (b - a) * p;

    markers.forEach((marker) => {
        const explicitSource = marker.querySelector<HTMLElement>('[data-cta-float-source]');
        const source = (explicitSource
            ?? (marker.matches('a[href]') ? marker : marker.querySelector('a[href]'))) as HTMLElement | null;

        if (!source) {
            return;
        }

        const dropdownId = source.getAttribute('data-dropdown-toggle');
        const isDropdown = !!dropdownId;

        const label = (source.textContent || '').trim() || source.getAttribute('aria-label') || 'Accéder';

        const fab: HTMLElement = isDropdown ? document.createElement('button') : document.createElement('a');
        if (isDropdown) {
            (fab as HTMLButtonElement).type = 'button';
            fab.setAttribute('aria-haspopup', 'true');
        } else {
            const link = source as HTMLAnchorElement;
            (fab as HTMLAnchorElement).href = link.href;
            const tgt = source.getAttribute('target');
            const rel = source.getAttribute('rel');
            if (tgt) {
                (fab as HTMLAnchorElement).target = tgt;
            }
            if (rel) {
                (fab as HTMLAnchorElement).rel = rel;
            }
        }
        fab.setAttribute('aria-label', label);
        fab.className = 'cta-float';

        Object.assign(fab.style, {
            position: 'fixed',
            top: '0px',
            left: '0px',
            height: '2rem',
            width: '2rem',
            boxSizing: 'border-box',
            overflow: 'hidden',
            border: 'none',
            padding: '0',
            font: 'inherit',
            appearance: 'none',
            borderRadius: '9999px',
            background: 'var(--color-primary-600)',
            color: 'var(--color-cloud-50, #ffffff)',
            outline: '1px solid var(--color-primary-600)',
            outlineOffset: '-1px',
            boxShadow: '0 0 8px 0 var(--color-onyx-200)', // ombre DS
            zIndex: '30',
            visibility: 'hidden', // hors focus/lecteurs d'écran tant qu'inactif
            opacity: '0',
            pointerEvents: 'none',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: HOVER_TRANSITION, // survol animé ; position pilotée par le scroll
        } as Partial<CSSStyleDeclaration>);

        const labelEl = document.createElement('span');
        labelEl.textContent = label;
        Object.assign(labelEl.style, {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            whiteSpace: 'nowrap',
            fontWeight: '500',
            fontSize: '1rem',
            lineHeight: '1',
        } as Partial<CSSStyleDeclaration>);

        const iconEl = document.createElement('span');
        iconEl.innerHTML = isDropdown ? CHEVRON : ARROW;
        Object.assign(iconEl.style, {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'inline-flex',
            lineHeight: '0',
            opacity: '0',
        } as Partial<CSSStyleDeclaration>);

        fab.append(labelEl, iconEl);
        document.body.appendChild(fab);

        // Effet DS (survol + focus clavier) : outline interne + halo.
        const setHover = (on: boolean): void => {
            fab.style.outlineWidth = on ? '4px' : '1px';
            fab.style.outlineOffset = on ? '-4px' : '-1px';
            fab.style.boxShadow = on ? '0 0 8px 0 var(--color-primary-600)' : '0 0 8px 0 var(--color-onyx-200)';
        };
        fab.addEventListener('mouseenter', () => setHover(true));
        fab.addEventListener('mouseleave', () => setHover(false));
        fab.addEventListener('focus', () => setHover(true));
        fab.addEventListener('blur', () => setHover(false));

        // CTA de type dropdown : on CLONE le menu pour le FAB. Le menu d'origine
        // reste piloté par Flowbite/Popper (bouton du haut) ; le clone, hors de
        // ce contrôle, s'ouvre en fixed SOUS le petit bouton sans conflit de
        // transform Popper.
        const srcMenu = isDropdown && dropdownId ? document.getElementById(dropdownId) : null;
        let fabMenu: HTMLElement | null = null;
        if (srcMenu) {
            fabMenu = srcMenu.cloneNode(true) as HTMLElement;
            fabMenu.removeAttribute('id');
            // Purge les styles inline hérités du menu d'origine (transform/inset/margin
            // posés par Popper via Flowbite) qui décaleraient le clone hors écran.
            fabMenu.removeAttribute('style');
            fabMenu.classList.add('hidden');
            Object.assign(fabMenu.style, {
                position: 'fixed',
                zIndex: '50',
            } as Partial<CSSStyleDeclaration>);
            document.body.appendChild(fabMenu);
        }

        const onDocClick = (ev: MouseEvent): void => {
            const t = ev.target as Node;
            if (fabMenu && !fabMenu.contains(t) && !fab.contains(t)) {
                closeFabMenu();
            }
        };

        // Cale le menu sous le FAB, aligné à droite. Rappelé à chaque frame du
        // morph pour que le menu suive le bouton.
        const positionFabMenu = (): void => {
            if (!fabMenu || fabMenu.classList.contains('hidden')) {
                return;
            }
            const fr = fab.getBoundingClientRect();
            const mr = fabMenu.getBoundingClientRect();
            fabMenu.style.left = `${Math.max(8, fr.right - mr.width)}px`;
            fabMenu.style.top = `${fr.bottom + 8}px`;
        };

        const openFabMenu = (): void => {
            if (!fabMenu || !fabMenu.classList.contains('hidden')) {
                return;
            }
            fabMenu.classList.remove('hidden');
            positionFabMenu();
            window.setTimeout(() => document.addEventListener('click', onDocClick, true), 0);
        };

        function closeFabMenu(): void {
            if (!fabMenu || fabMenu.classList.contains('hidden')) {
                return;
            }
            fabMenu.classList.add('hidden');
            document.removeEventListener('click', onDocClick, true);
        }

        // Referme le menu du haut via l'instance Flowbite quand elle existe,
        // pour garder son état interne cohérent.
        const isSourceMenuOpen = (): boolean => !!srcMenu && !srcMenu.classList.contains('hidden');
        const closeSourceMenu = (): void => {
            if (!srcMenu || !isSourceMenuOpen()) {
                return;
            }
            const instances = (window as unknown as {
                FlowbiteInstances?: { getInstance?: (t: string, id: string) => { hide?: () => void } | undefined };
            }).FlowbiteInstances;
            const instance = dropdownId ? instances?.getInstance?.('Dropdown', dropdownId) : undefined;
            if (instance?.hide) {
                instance.hide();
            } else {
                srcMenu.classList.add('hidden');
            }
        };

        if (fabMenu) {
            fab.addEventListener('click', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                if (fabMenu.classList.contains('hidden')) {
                    openFabMenu();
                } else {
                    closeFabMenu();
                }
            });
            // Fermeture après choix d'une modalité.
            fabMenu.querySelectorAll('a').forEach((lnk) => lnk.addEventListener('click', () => closeFabMenu()));
        }

        const update = (): void => {
            // garde la place même masqué (rect mesurable)
            const r = source.getBoundingClientRect();
            const p = clamp((navHeight + TRIGGER_OFFSET - r.top) / TRIGGER_OFFSET);
            const active = p > 0;
            // reduced-motion : pas de trajet (swap direct) ; sinon ease-out (ralentit en fin de course).
            const e = reduceMotion ? (active ? 1 : 0) : 1 - Math.pow(1 - p, 3);

            // Passage de relais : le menu ouvert sur le bouton du haut est rouvert
            // sous le FAB, pour que le choix reste accessible sans re-cliquer.
            if (active && isSourceMenuOpen()) {
                closeSourceMenu();
                openFabMenu();
            }

            source.style.visibility = active ? 'hidden' : '';
            fab.style.visibility = active ? 'visible' : 'hidden';
            fab.style.opacity = active ? '1' : '0';
            fab.style.pointerEvents = active ? 'auto' : 'none';

            if (!active) {
                // FAB masqué (retour en haut) : referme le menu éventuellement ouvert.
                closeFabMenu();
                return;
            }

            const contentRight = container
                ? container.getBoundingClientRect().right - containerPadRight
                : window.innerWidth - GAP;
            const screenDockLeft = window.innerWidth - GAP - r.height;
            // Place à droite des cards → bord de l'écran ; sinon à cheval sur le bord
            // du contenu, mais jamais au-delà de l'écran : sur mobile, où le contenu
            // occupe toute la largeur, le rond reste dans la marge latérale.
            const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
            const dockLeft = Math.min(
                screenDockLeft > contentRight ? screenDockLeft : contentRight - r.height / 2,
                viewportWidth - EDGE_MIN - r.height,
            );

            let dockTop = navHeight + TOP_OFFSET;
            if (footer) {
                dockTop = Math.min(dockTop, footer.getBoundingClientRect().top - FOOTER_PAD - r.height);
            }

            fab.style.height = `${r.height}px`;
            fab.style.width = `${lerp(r.width, r.height, e)}px`;
            fab.style.left = `${lerp(r.left, dockLeft, e)}px`;
            fab.style.top = `${lerp(r.top, dockTop, e)}px`;
            labelEl.style.opacity = `${clamp(1 - e * 1.6)}`;
            iconEl.style.opacity = `${clamp(e * 1.6 - 0.4)}`;

            // Le FAB bouge pendant le morph : le menu ouvert le suit.
            positionFabMenu();
        };

        let ticking = false;
        const onScroll = (): void => {
            if (ticking) {
                return;
            }
            ticking = true;
            window.requestAnimationFrame(() => {
                update();
                ticking = false;
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        update(); // état initial
    });
}
